import {
  type Customer,
  useConsent,
  useDraftOrder,
  useOpportunity,
  useOrder,
  useQuotation,
} from "@repo/api";
import { getTimezoneByStateCode } from "@repo/utils";
import isEmpty from "lodash/isEmpty";

import { useSettings } from "../settings/SettingsProvider";
import {
  getCustomerAddress,
  getCustomerEmail,
  getCustomerFromConsent,
  getCustomerPhone,
  mapCustomerDetailsFromBoka,
  mapCustomerFromDraftOrder,
  mapCustomerFromOrder,
  mapCustomerFromQuotation,
} from "../utils/customerHelpers";
import { useSearchOpportunityCustomer } from "./useSearchOpportunityCustomer";

export const useCustomer = (
  projectId?: number,
  orderId?: string,
  isAppointmentOrder?: boolean
) => {
  const { market } = useSettings();
  const { data: project } = useOpportunity(
    projectId ? Number(projectId) : undefined
  );

  const {
    data: customerSearchResult,
    isValidating: isValidatingCustomerSearch,
  } = useSearchOpportunityCustomer(projectId ? Number(projectId) : undefined);
  const { data: consentData, isValidating: isValidatingConsent } = useConsent(
    project?.partyId
  );

  const { data: orderData, isValidating: isValidatingOrder } = useOrder(
    projectId
      ? project?.orders?.allOrders?.find(
          (order) => order.isDraftOrder === false
        )?.orderId
      : orderId
        ? String(orderId)
        : undefined
  );

  const { data: draftOrderData, isValidating: isValidatingDraftOrder } =
    useDraftOrder(
      project?.orders?.allOrders?.find((order) => order.isDraftOrder === true)
        ?.orderId
    );

  const {
    data: appointmentOrderData,
    isValidating: isValidatingAppointmentOrder,
  } = useOrder(
    project?.appointmentDetails?.find((a) => !isEmpty(a.orderNumber))
      ?.orderNumber
  );

  const { data: quotationData, isValidating: isValidatingQuotation } =
    useQuotation(project?.quotations?.quotationInfo?.[0]?.id);

  // React hook cannot be called in conditionally, so moving this condition below all hooks
  if (!projectId && !orderId) {
    return {
      isValidating: false,
      customer: undefined,
      customerSource: undefined,
      editable: false,
    };
  }

  const consentCustomer = getCustomerFromConsent(consentData);

  let editable = true;
  let customer: Customer | undefined;
  let customerSource = customerSearchResult?.source;

  const searchedCustomer = customerSearchResult?.customer;
  const defaultCustomer = consentCustomer;
  const appointmentCustomer = project?.appointmentDetails?.[0]?.customer;
  const appointmentOrderCustomer = appointmentOrderData?.billingContact;

  const getOrderCustomer = () => {
    // if projectId is provided, make sure it's the same customer with same partyId
    if (projectId) {
      return orderData?.deliveryContact?.customerContactDetails
        .globalCustomerId === project?.partyId
        ? orderData?.deliveryContact
        : undefined;
    }
    // if no projectId is provided, we should use order customer

    return isAppointmentOrder
      ? orderData?.billingContact
      : orderData?.deliveryContact;
  };

  const orderCustomer = getOrderCustomer();

  const draftOrderCustomer =
    draftOrderData?.cartData?.customer?.partyId === project?.partyId
      ? draftOrderData?.cartData?.customer
      : undefined;

  const quotationCustomer =
    quotationData?.cartData?.customer?.partyId === project?.partyId
      ? quotationData?.cartData?.customer
      : undefined;

  const isValidating =
    isValidatingCustomerSearch ||
    isValidatingConsent ||
    isValidatingOrder ||
    isValidatingDraftOrder ||
    isValidatingAppointmentOrder ||
    isValidatingQuotation;

  // No customer found from customer search, fallback to other sources
  if (!isValidating && !searchedCustomer) {
    editable = false;

    if (appointmentCustomer) {
      customerSource = "BOKA";
      customer = {
        ...defaultCustomer,
        ...mapCustomerDetailsFromBoka(appointmentCustomer),
      };
    } else if (orderData && orderCustomer) {
      customerSource = "ORDER";
      customer = {
        ...defaultCustomer,
        ...mapCustomerFromOrder(orderData, isAppointmentOrder ?? false),
      };
    } else if (draftOrderCustomer) {
      customerSource = "DRAFT_ORDER";
      customer = {
        ...defaultCustomer,
        ...mapCustomerFromDraftOrder(draftOrderCustomer),
      };
    } else if (quotationCustomer) {
      customerSource = "QUOTATION";
      customer = {
        ...defaultCustomer,
        ...mapCustomerFromQuotation(quotationCustomer),
      };
    } else if (consentCustomer) {
      customerSource = "CONSENT";
      customer = consentCustomer;
    }
  } else {
    customer = searchedCustomer!;
  }

  // If customer is still undefined, return early
  if (!customer) {
    return {
      isValidating,
      customer: undefined,
      customerSource: undefined,
      editable: false,
    };
  }

  // Aggregate address
  const address = getCustomerAddress(
    customer,
    orderCustomer,
    draftOrderCustomer,
    appointmentOrderCustomer,
    quotationCustomer
  );

  const timezone = address.state
    ? getTimezoneByStateCode(market, address.state)
    : undefined;

  // Add missing info
  customer = {
    ...customer,
    ...address,
    email: getCustomerEmail(
      customer,
      appointmentCustomer,
      orderCustomer,
      draftOrderCustomer,
      appointmentOrderCustomer,
      quotationCustomer
    ),
    phoneNumber: getCustomerPhone(
      customer,
      appointmentCustomer,
      orderCustomer,
      draftOrderCustomer,
      appointmentOrderCustomer,
      quotationCustomer
    ),
    customerType:
      customer?.customerType ?? project?.customerType ?? "Individual",
    partyId: customer?.partyId ?? project?.partyId ?? "",
    timezone,
  };

  return { isValidating, customer, customerSource, editable };
};
